
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Mail, Lock, LogIn, MailCheck, MailQuestion } from "lucide-react";
import { useTranslation } from "react-i18next";
import Hey from "@/lib/hey-utils";
import { trackAuthAction } from "@/utils/analytics";

interface PasswordLoginProps {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  navigate: (path: string) => void;
  isValidEmail: (email: string) => boolean;
  setAuthMethod: (method: "password" | "otp" | "register") => void;
}

export const PasswordLogin = ({
  isLoading,
  setIsLoading,
  email,
  setEmail,
  password,
  setPassword,
  navigate,
  isValidEmail,
  setAuthMethod
}: PasswordLoginProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [showConfirmEmailAlert, setShowConfirmEmailAlert] = useState(false);

  const handlePasswordAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setShowConfirmEmailAlert(false);

    try {
      trackAuthAction("password_login");
      const trimmedEmail = email.trim().toLowerCase();
      localStorage.setItem("psw_lastUsedUserEmail", trimmedEmail);
      let result = await Hey.HeyUtils.getInstance().loginUserPassword(email, password);
      if (result?.success) {
        trackAuthAction("password_login", "success");
        // toast({
        //   title: t("common.toast.success"),
        //   description: t("auth.loginSuccess"),
        // });
        navigate("/");
      } else {
        trackAuthAction("password_login", "failed", result.error);
        throw new Error(t("auth.incorrectCredentials"));
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("common.toast.error"),
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };


    useEffect(() => {
      setEmail(localStorage.getItem("psw_lastUsedUserEmail") || "");
    }, []);

  return (
    <form onSubmit={handlePasswordAuth} className="mt-8 space-y-6">
      <div className="space-y-4">
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            type="email"
            placeholder={t("settings.profile.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`pl-10 ${!isValidEmail(email) && email.length > 0 ? 'border-red-500' : ''}`}
            required
          />
          {email.length > 0 && !isValidEmail(email) && (
            <p className="mt-1 text-sm text-red-500">{t("auth.invalidEmail")}</p>
          )}
        </div>

        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            type="password"
            placeholder={t("auth.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pl-10"
            required
            minLength={6}
          />
        </div>
      </div>

      <Button 
        className="w-full" 
        type="submit" 
        disabled={isLoading || (email.length > 0 && !isValidEmail(email))}
        style={{ backgroundColor: "#000835" }}
      >
        {isLoading ? (
          t("common.updating")
        ) : 
          <>
            <LogIn className="w-4 h-4 mr-2" />
            {t("auth.signIn")}
          </>
        }
      </Button>
      
      
      <Button 
        className="w-full" 
        variant="secondary"
        type="button"
        onClick={() => {
          trackAuthAction("forgot_password");
          setAuthMethod("otp");
          toast({
            title: t("common.toast.info"),
            description: t("auth.forgotPasswordInstructions"),
          });
        }}>
        <MailQuestion className="w-4 h-4 mr-2" />
        {t("auth.forgotPassword")}
      </Button>
    </form>
  );
};
